import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import MainCard from 'components/MainCard';
import useApi from 'hooks/useApi';
import { PermissionWrapper } from 'components/PermissionComponents';
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';

/**
 * Detection targets — the classes the mobile capture gate can recognise.
 *
 * This page exists so adding a class (a POD document, a lock, a seal) is an
 * upload rather than an app release. The app ships one shared CLIP encoder; a
 * class is only its label fingerprints, produced by export_mobileclip.py and
 * stored here. Phones fetch them by name and cache them.
 *
 * Targets are platform-wide, not per process: this tab sits inside a process
 * because that is where detection is configured, but a class uploaded here is
 * available to every process.
 *
 * Reads are open to anyone signed in — the flow builder lists targets too.
 * Writes need the "Add/edit Object Detection" permission, enforced by the API as
 * well as hidden here: an upload changes behaviour on every installed app at
 * once, so a dashboard-only gate would not be enough.
 */

interface TargetRow {
  name: string;
  label?: string;
  model?: string;
  pretrained?: string;
  dim?: number;
  labels?: { name: string; group: string }[];
  defaultFraming?: { shape?: string; aspectRatio?: number; minFill?: number };
  defaultUseQuad?: boolean;
  defaultCameraFacing?: string;
  defaultNoun?: string;
  updatedAt?: string;
}

const SHAPES = ['card', 'a4', 'plate', 'object', 'face'];

/**
 * The axios interceptor (utils/axios.ts) rejects with the response BODY, not an
 * AxiosError — so `e.response.data.message` is always undefined here and every
 * failure rendered as the generic fallback, hiding the real reason (a 403 looked
 * identical to a 500). Read the envelope the interceptor actually hands us.
 */
const reason = (e: any, fallback: string): string =>
  (typeof e === 'string' && e) || e?.message || e?.response?.data?.message || fallback;

interface ObjectDetectionSettingsProps {
  teamUid?: string;
  clientUID?: string;
}

export default function ObjectDetectionSettings({ teamUid, clientUID }: ObjectDetectionSettingsProps) {
  const { getDetectionTargets, saveDetectionTarget, deleteDetectionTarget } = useApi();
  const canManage = useHasPermission(PERMISSIONS.ADD_EDIT_OBJECT_DETECTION);

  const [rows, setRows] = useState<TargetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  // The parsed upload, held until the admin confirms its guide-box defaults.
  const [pending, setPending] = useState<any>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDetectionTargets();
      setRows(Array.isArray(res?.data) ? res.data : []);
      setError('');
    } catch (e: any) {
      setError(reason(e, 'Could not load detection targets'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // let the same file be picked again after a failure
    if (!file) return;
    setNotice('');
    try {
      const parsed = JSON.parse(await file.text());
      if (!parsed?.target || !Array.isArray(parsed?.labels)) {
        setError('That file is not a prototype export (expected "target" and "labels").');
        return;
      }
      // Everything else — the model stamp, vector widths, unit length, at least
      // one accept label — is checked by the API, which reports every problem at
      // once. Re-implementing those rules here would only let them drift.
      setError('');
      setPending({
        ...parsed,
        label: parsed.target.replace(/_/g, ' '),
        defaultFraming: { shape: 'card', aspectRatio: 1.586, minFill: 0.6 },
        defaultUseQuad: true,
        defaultCameraFacing: 'env',
        defaultNoun: parsed.target.replace(/_/g, ' ')
      });
    } catch {
      setError('That file is not valid JSON.');
    }
  };

  const save = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await saveDetectionTarget(pending.target, pending);
      setPending(null);
      setError('');
      setNotice(
        `"${pending.target}" saved. A phone that has never seen it fetches it on ` +
        `first use; one already using it picks up the change within 15 minutes.`,
      );
      await load();
    } catch (e: any) {
      setError(reason(e, 'Could not save this target'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (name: string) => {
    try {
      await deleteDetectionTarget(name);
      setNotice(`"${name}" removed.`);
      await load();
    } catch (e: any) {
      setError(reason(e, 'Could not remove this target'));
    }
  };

  const setFraming = (key: string, value: any) =>
    setPending((p: any) => ({ ...p, defaultFraming: { ...p.defaultFraming, [key]: value } }));

  return (
    <MainCard title="Detection targets">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What the mobile camera can auto-detect. Generate a target with
        <code> python export_mobileclip.py --only &lt;name&gt;</code> and upload the
        JSON it writes — no app release is needed for a new or re-tuned class.
      </Typography>

      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can see the classes but not change them — that needs the
          "Add/edit Object Detection" permission on your role.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_OBJECT_DETECTION} disableInsteadOfHide={true}>
          <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => fileInput.current?.click()}>
            Upload prototypes
          </Button>
        </PermissionWrapper>
        <input ref={fileInput} type="file" accept="application/json,.json" hidden onChange={onFile} />
      </Stack>

      {pending && (
        <MainCard sx={{ mb: 2 }} title={`New target: ${pending.target}`}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {pending.labels.length} labels · {pending.model || 'no model stamp'} /{' '}
            {pending.pretrained || '—'} · {pending.dim || pending.labels?.[0]?.embedding?.length} dims
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {pending.labels.map((l: any) => (
              <Chip key={l.name} size="small" label={l.name}
                color={l.group === 'accept' ? 'success' : 'default'} />
            ))}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField size="small" label="Shown as" value={pending.label}
              onChange={(e) => setPending({ ...pending, label: e.target.value })} />
            <TextField size="small" label="Called on the phone" value={pending.defaultNoun}
              helperText={`"That doesn't look like a ${pending.defaultNoun}"`}
              onChange={(e) => setPending({ ...pending, defaultNoun: e.target.value })} />
            <TextField select size="small" label="Guide box" sx={{ minWidth: 120 }}
              value={pending.defaultFraming.shape}
              onChange={(e) => setFraming('shape', e.target.value)}>
              {SHAPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField size="small" type="number" label="Aspect ratio" sx={{ width: 130 }}
              inputProps={{ step: 0.01, min: 0.1 }}
              value={pending.defaultFraming.aspectRatio}
              onChange={(e) => setFraming('aspectRatio', parseFloat(e.target.value))} />
            <TextField size="small" type="number" label="Min fill" sx={{ width: 110 }}
              inputProps={{ step: 0.05, min: 0, max: 1 }}
              value={pending.defaultFraming.minFill}
              onChange={(e) => setFraming('minFill', parseFloat(e.target.value))} />
            <TextField select size="small" label="Camera" sx={{ minWidth: 120 }}
              value={pending.defaultCameraFacing}
              onChange={(e) => setPending({ ...pending, defaultCameraFacing: e.target.value })}>
              <MenuItem value="env">Back</MenuItem>
              <MenuItem value="user">Front (selfie)</MenuItem>
            </TextField>
          </Stack>

          <FormControlLabel
            control={
              <Checkbox checked={pending.defaultUseQuad}
                onChange={(e) => setPending({ ...pending, defaultUseQuad: e.target.checked })} />
            }
            label="Use the document-edge detector"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Leave this ON for anything rectangular (a card, a sheet of paper). Turn it
            OFF for shapes with no straight edges — a face, a padlock — where it finds
            nothing and the capture would never trigger.
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save target'}
            </Button>
            <Button onClick={() => setPending(null)} disabled={saving}>Cancel</Button>
          </Stack>
        </MainCard>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Shown as</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Labels</TableCell>
              <TableCell>Guide box</TableCell>
              <TableCell>Edge detector</TableCell>
              <TableCell>Camera</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No targets uploaded yet. Until one is, phones fall back to the
                    classes bundled in the app they are running.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((t) => (
              <TableRow key={t.name} hover>
                <TableCell><code>{t.name}</code></TableCell>
                <TableCell>{t.label || '—'}</TableCell>
                <TableCell>
                  <Tooltip title={`${t.model} / ${t.pretrained}`}>
                    <span>{t.dim} dims</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{t.labels?.length ?? 0}</TableCell>
                <TableCell>{t.defaultFraming?.shape ?? '—'}</TableCell>
                <TableCell>{t.defaultUseQuad === false ? 'off' : 'on'}</TableCell>
                <TableCell>{t.defaultCameraFacing === 'user' ? 'front' : 'back'}</TableCell>
                <TableCell align="right">
                  <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_OBJECT_DETECTION} disableInsteadOfHide={true}>
                    <IconButton size="small" onClick={() => remove(t.name)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </PermissionWrapper>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </MainCard>
  );
}
